// src/pages/admin/reports/Categories.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
} from "@material-tailwind/react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../../firebaseConfig";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

export default function Categories() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const [edit, setEdit] = useState(null); // {id, name, description}
  const [del, setDel] = useState(null); // {id, name}
  const [saving, setSaving] = useState(false);

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow: "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: "0 0 0 4px rgba(135,135,135,0.18)",
    };
  }, []);

  const inputCls =
    "w-full rounded-2xl border px-3 py-3 outline-none transition font-tajawal bg-white";
  const btnPrimary =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition disabled:opacity-60";
  const btnGhost =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const snap = await getDocs(collection(db, "categories"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ar"));
      setList(rows);
    } catch {
      setErr("تعذّر جلب الأقسام.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addCategory(e) {
    e.preventDefault();
    if (!name.trim()) {
      setErr("أدخل اسم القسم.");
      return;
    }
    setSaving(true);
    setErr("");
    setOk("");
    try {
      await addDoc(collection(db, "categories"), {
        name: name.trim(),
        description: desc.trim() || "",
        createdAt: serverTimestamp(),
        ownerId: user?.uid || null,
      });
      setName("");
      setDesc("");
      setOk("تمت إضافة القسم.");
      await load();
    } catch {
      setErr("فشل إضافة القسم.");
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit() {
    if (!edit?.id || !edit?.name?.trim()) {
      setErr("اسم القسم مطلوب.");
      return;
    }
    setSaving(true);
    setErr("");
    setOk("");
    try {
      await updateDoc(doc(db, "categories", edit.id), {
        name: edit.name.trim(),
        description: edit.description?.trim() || "",
      });
      setEdit(null);
      setOk("تم حفظ التعديل.");
      await load();
    } catch {
      setErr("تعذّر حفظ التعديل.");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!del?.id) return;
    setSaving(true);
    setErr("");
    setOk("");
    try {
      await deleteDoc(doc(db, "categories", del.id));
      setDel(null);
      setOk("تم حذف القسم.");
      await load();
    } catch {
      setErr("تعذّر الحذف.");
    } finally {
      setSaving(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) =>
        String(c.name || "").toLowerCase().includes(q) ||
        String(c.description || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  return (
    <div dir="rtl" className="min-h-screen p-4" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-6xl rounded-3xl overflow-hidden bg-white"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div className="flex items-center justify-between px-6 py-6" style={{ background: ui.headerBg }}>
            <button
              onClick={handleBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5 transition group-hover:translate-x-0.5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography variant="h4" className="text-white font-extrabold font-tajawal">
                الأقسام
              </Typography>
              <div className="mt-1 text-xs" style={{ color: "rgba(255,255,255,0.78)" }}>
                إضافة / تعديل / حذف — ألوان الصورة فقط
              </div>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-4 p-6">
          {(err || ok) && (
            <div className="space-y-2">
              {err && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal"
                  style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
                >
                  {err}
                </div>
              )}
              {ok && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal flex items-center gap-2"
                  style={{ background: BRAND.white, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {ok}
                </div>
              )}
            </div>
          )}

          {/* نموذج إضافة */}
          <form
            onSubmit={addCategory}
            className="rounded-3xl p-4 border grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
            style={{ borderColor: BRAND.paper, backgroundColor: BRAND.white }}
          >
            <div>
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                اسم القسم *
              </label>
              <input
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="مثال: مشروبات"
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                وصف (اختياري)
              </label>
              <input
                className={inputCls}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="وصف مختصر"
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              />
            </div>

            <div className="md:col-span-3 flex justify-end gap-2">
              <button
                type="submit"
                disabled={saving}
                className={btnPrimary}
                style={{
                  background: ui.btnBg,
                  color: BRAND.white,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                <PlusIcon className="h-5 w-5" />
                إضافة
              </button>

              <button
                type="button"
                onClick={load}
                disabled={loading}
                className={btnGhost}
                style={{
                  background: BRAND.white,
                  color: BRAND.ink,
                  border: `1px solid ${BRAND.mist}`,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                <ArrowPathIcon className="h-5 w-5" />
                تحديث
              </button>
            </div>
          </form>

          {/* بحث */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                بحث
              </label>
              <input
                className={inputCls}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ابحث بالاسم أو الوصف"
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              />
            </div>
          </div>

          {/* جدول */}
          <div className="overflow-auto rounded-2xl border" style={{ borderColor: BRAND.paper }}>
            <table className="w-full min-w-max text-right font-tajawal">
              <thead>
                <tr style={{ backgroundColor: BRAND.paper }}>
                  <th className="p-3 text-sm font-extrabold" style={{ color: BRAND.ink }}>
                    الاسم
                  </th>
                  <th className="p-3 text-sm font-extrabold" style={{ color: BRAND.ink }}>
                    الوصف
                  </th>
                  <th className="p-3 text-sm font-extrabold w-40" style={{ color: BRAND.ink }}>
                    إجراءات
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.length ? (
                  filtered.map((cat, idx) => (
                    <tr
                      key={cat.id}
                      className="border-t"
                      style={{
                        borderColor: BRAND.paper,
                        background: idx % 2 === 0 ? BRAND.white : "rgba(239,239,239,0.30)",
                      }}
                    >
                      <td className="p-3">{cat.name || "-"}</td>
                      <td className="p-3">{cat.description || "-"}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 justify-start">
                          <button
                            className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-extrabold transition"
                            style={{
                              background: BRAND.white,
                              border: `1px solid ${BRAND.mist}`,
                              color: BRAND.ink,
                            }}
                            onClick={() =>
                              setEdit({
                                id: cat.id,
                                name: cat.name || "",
                                description: cat.description || "",
                              })
                            }
                          >
                            <PencilIcon className="h-4 w-4" />
                            تعديل
                          </button>

                          <button
                            className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-extrabold transition"
                            style={{
                              background: BRAND.paper,
                              border: `1px solid ${BRAND.mist}`,
                              color: BRAND.ink,
                            }}
                            onClick={() => setDel({ id: cat.id, name: cat.name || "" })}
                          >
                            <TrashIcon className="h-4 w-4" />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="p-6 text-center" style={{ color: BRAND.graphite }}>
                      لا توجد أقسام.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>

        <CardFooter className="p-4" />
      </Card>

      {/* تعديل */}
      <Dialog open={!!edit} handler={() => setEdit(null)} size="sm">
        <DialogHeader className="justify-between">
          <Typography className="font-tajawal" variant="h6" style={{ color: BRAND.ink }}>
            تعديل القسم
          </Typography>
          <IconButton variant="text" onClick={() => setEdit(null)}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody divider>
          <div className="space-y-3">
            <label className="block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              الاسم
            </label>
            <input
              className={inputCls}
              value={edit?.name || ""}
              onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />

            <label className="block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              الوصف
            </label>
            <input
              className={inputCls}
              value={edit?.description || ""}
              onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />
          </div>
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <button
            className={btnGhost}
            style={{ background: BRAND.white, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
            onClick={() => setEdit(null)}
          >
            إلغاء
          </button>
          <button
            className={btnPrimary}
            style={{ background: ui.btnBg, color: BRAND.white }}
            onClick={saveEdit}
            disabled={saving}
          >
            حفظ
          </button>
        </DialogFooter>
      </Dialog>

      {/* حذف */}
      <Dialog open={!!del} handler={() => setDel(null)} size="sm">
        <DialogHeader className="justify-between">
          <Typography className="font-tajawal" variant="h6" style={{ color: BRAND.ink }}>
            تأكيد الحذف
          </Typography>
          <IconButton variant="text" onClick={() => setDel(null)}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody className="font-tajawal" divider style={{ color: BRAND.ink }}>
          هل تريد حذف القسم <b>{del?.name}</b>؟
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <button
            className={btnGhost}
            style={{ background: BRAND.white, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
            onClick={() => setDel(null)}
          >
            إلغاء
          </button>
          <button
            className={btnPrimary}
            style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
            onClick={confirmDelete}
            disabled={saving}
          >
            حذف
          </button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
